import React, { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { usersAPI } from '../utils/api';
import { 
  FaUser, 
  FaEnvelope, 
  FaPhone, 
  FaCalendarAlt, 
  FaEdit, 
  FaSave, 
  FaTimes, 
  FaShoppingCart, 
  FaHeart, 
  FaClipboardList, 
  FaChartLine, 
  FaCog, 
  FaShieldAlt,
  FaGift,
  FaStar,
  FaMapMarkerAlt,
  FaLock,
  FaCheck,
  FaTimesCircle
} from 'react-icons/fa';

const ProfileContainer = styled.div`
  max-width: 1200px;
  margin: 40px auto;
  padding: 0 20px;
  
  @media (max-width: 768px) {
    margin: 20px auto;
    padding: 0 15px;
  }
`;

const ProfileHeader = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  color: #333;
  padding: 50px 40px;
  border-radius: 20px;
  margin-bottom: 40px;
  text-align: center;
  position: relative;
  overflow: hidden;
  border: 1px solid #dee2e6;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255,107,53,0.05) 0%, transparent 70%);
    animation: float 6s ease-in-out infinite;
  }
  
  @keyframes float {
    0%, 100% { transform: translateY(0px) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(180deg); }
  }
  
  @media (max-width: 768px) {
    padding: 30px 20px;
  }
`;

const ProfileTitle = styled.h1`
  font-size: 42px;
  margin-bottom: 15px;
  font-weight: 700;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const ProfileSubtitle = styled.p`
  font-size: 18px;
  opacity: 0.95;
  margin: 0;
  position: relative;
  z-index: 1;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ProfileContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 30px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 25px;
  }
`;

const ProfileSection = styled.div`
  background: white;
  padding: 35px;
  border-radius: 15px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  border: 1px solid #f0f0f0;
`;

const SectionTitle = styled.h2`
  color: #333;
  font-size: 28px;
  margin-bottom: 25px;
  display: flex;
  align-items: center;
  gap: 12px;
  border-bottom: 3px solid #ff6b35;
  padding-bottom: 15px;
  
  svg {
    color: #ff6b35;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 25px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-weight: 600;
  color: #555;
  font-size: 15px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    color: #ff6b35;
    font-size: 14px;
  }
`;

const Input = styled.input`
  padding: 15px;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  font-size: 16px;
  transition: all 0.3s ease;
  background: #fafafa;
  
  &:focus {
    border-color: #ff6b35;
    outline: none;
    background: white;
    box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.1);
  }
  
  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
    color: #666;
  }
`;

const Button = styled.button`
  background: linear-gradient(135deg, #ff6b35 0%, #ff8c42 100%);
  color: white;
  padding: 15px 25px;
  border: none;
  border-radius: 10px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  text-decoration: none;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(255, 107, 53, 0.3);
  }
  
  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  &.secondary {
    background: transparent;
    color: #ff6b35;
    border: 2px solid #ff6b35;
  }
  
  &.secondary:hover {
    background: #ff6b35;
    color: white;
  }
  
  &.outline {
    background: transparent;
    color: #666;
    border: 2px solid #ddd;
  }
  
  &.outline:hover {
    border-color: #ff6b35;
    color: #ff6b35;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 25px;
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 0;
  border-bottom: 1px solid #f0f0f0;
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-weight: 500;
  color: #666;
  display: flex;
  align-items: center;
  gap: 10px;
  
  svg {
    color: #ff6b35;
    font-size: 16px;
  }
`;

const InfoValue = styled.span`
  color: #333;
  font-weight: 600;
  font-size: 15px;
`;

const Message = styled.div`
  padding: 15px;
  border-radius: 10px;
  margin-bottom: 25px;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
  
  &.success {
    background: linear-gradient(135deg, #e8f5e8 0%, #d4edda 100%);
    color: #155724;
    border: 1px solid #c3e6cb;
  }
  
  &.error {
    background: linear-gradient(135deg, #ffebee 0%, #f8d7da 100%);
    color: #721c24;
    border: 1px solid #f5c6cb;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 25px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 20px;
  border-radius: 12px;
  text-align: center;
  border: 1px solid #dee2e6;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  }
`;

const StatIcon = styled.div`
  font-size: 24px;
  color: #ff6b35;
`;

const StatNumber = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: #333;
  margin-bottom: 5px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  color: #666;
  font-weight: 500;
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin-top: 25px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ActionCard = styled(Link)`
  background: linear-gradient(135deg, #fff 0%, #f8f9fa 100%);
  padding: 20px;
  border-radius: 12px;
  text-decoration: none;
  color: inherit;
  border: 2px solid #e9ecef;
  transition: all 0.3s ease;
  text-align: center;
  
  &:hover {
    border-color: #ff6b35;
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(255, 107, 53, 0.15);
    color: inherit;
  }
`;

const ActionIcon = styled.div`
  font-size: 24px;
  color: #ff6b35;
  margin-bottom: 10px;
`;

const ActionTitle = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 5px;
`;

const ActionDesc = styled.div`
  font-size: 12px;
  color: #666;
`;

const ValidationHint = styled.div`
  font-size: 13px;
  color: #666;
  margin-top: 8px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 3px solid #ff6b35;
`;

const ValidationRule = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  color: ${props => props.valid ? '#28a745' : '#666'};
  font-size: 13px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  svg {
    font-size: 12px;
    flex-shrink: 0;
  }
`;

const Profile = () => {
  const { user, isAuthenticated, updateProfile, changePassword } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState({ text: '', type: '' });
  const [stats, setStats] = useState({
    totalOrders: 0,
    activeOrders: 0,
    favoritesCount: 0,
    reviewsCount: 0
  });
  const [statsLoading, setStatsLoading] = useState(true);
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
    email: user?.email || '',
    city: user?.city || ''
  });

  // Загрузка статистики активности
  useEffect(() => {
    const loadStats = async () => {
      if (!isAuthenticated()) return;
      
      try {
        setStatsLoading(true);
        const response = await usersAPI.getStats();
        setStats({
          totalOrders: response.data.totalOrders || 0,
          activeOrders: response.data.activeOrders || 0,
          favoritesCount: response.data.favoritesCount || 0,
          reviewsCount: response.data.reviewsCount || 0
        });
      } catch (error) {
        console.error('Error loading user stats:', error);
        // В случае ошибки оставляем значения по умолчанию (0)
      } finally {
        setStatsLoading(false);
      }
    };

    loadStats();
  }, [isAuthenticated]);

  // Если пользователь не авторизован, перенаправляем на главную
  if (!isAuthenticated()) {
    return <Navigate to="/" replace />;
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setMessage({ text: '', type: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ text: '', type: '' });

    try {
      const result = await updateProfile({
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        city: formData.city
      });

      if (result.success) {
        setMessage({ text: result.message, type: 'success' });
        setIsEditing(false);
      } else {
        setMessage({ text: result.message, type: 'error' });
      }
    } catch (error) {
      setMessage({ text: 'Произошла ошибка при обновлении профиля', type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: user?.phone || '',
      email: user?.email || '',
      city: user?.city || ''
    });
    setIsEditing(false);
    setMessage({ text: '', type: '' });
    // Также сбрасываем состояние смены пароля
    setIsChangingPassword(false);
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setPasswordMessage({ text: '', type: '' });
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
    setPasswordMessage({ text: '', type: '' });
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordMessage({ text: '', type: '' });

    if (!passwordData.currentPassword || !passwordData.currentPassword.trim()) {
      setPasswordMessage({ text: 'Введите текущий пароль', type: 'error' });
      return;
    }

    if (!passwordData.newPassword || passwordData.newPassword.length < 6) {
      setPasswordMessage({ text: 'Пароль должен содержать минимум 6 символов', type: 'error' });
      return;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(passwordData.newPassword)) {
      setPasswordMessage({ text: 'Пароль должен содержать строчные и заглавные буквы, а также цифры', type: 'error' });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordMessage({ text: 'Пароли не совпадают', type: 'error' });
      return;
    }

    setPasswordLoading(true);
    try {
      const result = await changePassword(passwordData.currentPassword.trim(), passwordData.newPassword);
      
      if (result.success) {
        setPasswordMessage({ text: result.message || 'Пароль успешно изменен', type: 'success' });
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setTimeout(() => {
          setIsChangingPassword(false);
          setPasswordMessage({ text: '', type: '' });
        }, 2000);
      } else {
        setPasswordMessage({ text: result.message || 'Ошибка при изменении пароля', type: 'error' });
      }
    } catch (error) {
      setPasswordMessage({ text: 'Произошла ошибка при изменении пароля', type: 'error' });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleCancelPasswordChange = () => {
    setPasswordData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setIsChangingPassword(false);
    setPasswordMessage({ text: '', type: '' });
  };

  // Функции для проверки валидации пароля
  const validatePassword = () => {
    const password = passwordData.newPassword;
    return {
      minLength: password.length >= 6,
      hasLowerCase: /[a-z]/.test(password),
      hasUpperCase: /[A-Z]/.test(password),
      hasDigit: /\d/.test(password),
      passwordsMatch: passwordData.newPassword === passwordData.confirmPassword && passwordData.confirmPassword.length > 0
    };
  };

  return (
    <ProfileContainer>
      <ProfileHeader>
        <ProfileTitle>Личный профиль</ProfileTitle>
        <ProfileSubtitle>
          Управляйте своими данными, отслеживайте активность и настройте аккаунт
        </ProfileSubtitle>
      </ProfileHeader>

      <ProfileContent>
        <ProfileSection>
          <SectionTitle>
            <FaUser />
            Информация о профиле
          </SectionTitle>
          
          {message.text && (
            <Message className={message.type}>
              {message.type === 'success' ? '✅' : '❌'} {message.text}
            </Message>
          )}

          {isEditing ? (
            <Form onSubmit={handleSubmit}>
              <InputGroup>
                <Label>
                  <FaUser />
                  Имя
                </Label>
                <Input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  placeholder="Введите ваше имя"
                />
              </InputGroup>

              <InputGroup>
                <Label>
                  <FaUser />
                  Фамилия
                </Label>
                <Input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  placeholder="Введите вашу фамилию"
                />
              </InputGroup>

              <InputGroup>
                <Label>
                  <FaPhone />
                  Телефон
                </Label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+7 (999) 123-45-67"
                />
              </InputGroup>

              <InputGroup>
                <Label>
                  <FaMapMarkerAlt />
                  Город
                </Label>
                <Input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="Введите ваш город"
                />
              </InputGroup>

              <InputGroup>
                <Label>
                  <FaEnvelope />
                  Email
                </Label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  disabled
                />
              </InputGroup>

              {/* Секция смены пароля */}
              <div style={{ marginTop: '30px', paddingTop: '30px', borderTop: '1px solid #eee' }}>
                <SectionTitle style={{ marginBottom: '20px', fontSize: '18px' }}>
                  <FaLock />
                  Смена пароля
                </SectionTitle>
                
                {passwordMessage.text && (
                  <Message className={passwordMessage.type} style={{ marginBottom: '20px' }}>
                    {passwordMessage.type === 'success' ? '✅' : '❌'} {passwordMessage.text}
                  </Message>
                )}

                {isChangingPassword ? (
                  <>
                    <InputGroup>
                      <Label>
                        <FaLock />
                        Текущий пароль
                      </Label>
                      <Input
                        type="password"
                        name="currentPassword"
                        value={passwordData.currentPassword}
                        onChange={handlePasswordChange}
                        placeholder="Введите текущий пароль"
                        disabled={isLoading || passwordLoading}
                      />
                    </InputGroup>
                    <InputGroup>
                      <Label>
                        <FaLock />
                        Новый пароль
                      </Label>
                      <Input
                        type="password"
                        name="newPassword"
                        value={passwordData.newPassword}
                        onChange={handlePasswordChange}
                        placeholder="Введите новый пароль"
                        disabled={isLoading || passwordLoading}
                      />
                      <ValidationHint>
                        <div style={{ fontWeight: 600, marginBottom: '10px', color: '#333' }}>Требования к паролю:</div>
                        <ValidationRule valid={validatePassword().minLength}>
                          {validatePassword().minLength ? <FaCheck /> : <FaTimesCircle />}
                          Минимум 6 символов
                        </ValidationRule>
                        <ValidationRule valid={validatePassword().hasLowerCase}>
                          {validatePassword().hasLowerCase ? <FaCheck /> : <FaTimesCircle />}
                          Содержит строчные буквы (a-z)
                        </ValidationRule>
                        <ValidationRule valid={validatePassword().hasUpperCase}>
                          {validatePassword().hasUpperCase ? <FaCheck /> : <FaTimesCircle />}
                          Содержит заглавные буквы (A-Z)
                        </ValidationRule>
                        <ValidationRule valid={validatePassword().hasDigit}>
                          {validatePassword().hasDigit ? <FaCheck /> : <FaTimesCircle />}
                          Содержит цифры (0-9)
                        </ValidationRule>
                      </ValidationHint>
                    </InputGroup>

                    <InputGroup>
                      <Label>
                        <FaLock />
                        Подтвердите пароль
                      </Label>
                      <Input
                        type="password"
                        name="confirmPassword"
                        value={passwordData.confirmPassword}
                        onChange={handlePasswordChange}
                        placeholder="Повторите новый пароль"
                        disabled={isLoading || passwordLoading}
                      />
                      {passwordData.confirmPassword.length > 0 && (
                        <ValidationHint>
                          <ValidationRule valid={validatePassword().passwordsMatch}>
                            {validatePassword().passwordsMatch ? <FaCheck /> : <FaTimesCircle />}
                            {validatePassword().passwordsMatch 
                              ? 'Пароли совпадают' 
                              : 'Пароли не совпадают'}
                          </ValidationRule>
                        </ValidationHint>
                      )}
                    </InputGroup>

                    <ButtonGroup style={{ marginTop: '10px' }}>
                      <Button 
                        type="button" 
                        onClick={handlePasswordSubmit}
                        disabled={isLoading || passwordLoading}
                      >
                        <FaSave />
                        {passwordLoading ? 'Сохранение...' : 'Изменить пароль'}
                      </Button>
                      <Button 
                        type="button" 
                        className="secondary" 
                        onClick={handleCancelPasswordChange}
                        disabled={isLoading || passwordLoading}
                      >
                        <FaTimes />
                        Отменить смену пароля
                      </Button>
                    </ButtonGroup>
                  </>
                ) : (
                  <ButtonGroup>
                    <Button 
                      type="button" 
                      onClick={() => setIsChangingPassword(true)}
                      disabled={isLoading}
                    >
                      <FaEdit />
                      Изменить пароль
                    </Button>
                  </ButtonGroup>
                )}
              </div>

              <ButtonGroup style={{ marginTop: '30px' }}>
                <Button type="submit" disabled={isLoading}>
                  <FaSave />
                  {isLoading ? 'Сохранение...' : 'Сохранить изменения'}
                </Button>
                <Button 
                  type="button" 
                  className="secondary" 
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  <FaTimes />
                  Отменить
                </Button>
              </ButtonGroup>
            </Form>
          ) : (
            <>
              <InfoItem>
                <InfoLabel>
                  <FaUser />
                  Имя:
                </InfoLabel>
                <InfoValue>{user?.firstName || 'Не указано'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>
                  <FaUser />
                  Фамилия:
                </InfoLabel>
                <InfoValue>{user?.lastName || 'Не указано'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>
                  <FaPhone />
                  Телефон:
                </InfoLabel>
                <InfoValue>{user?.phone || 'Не указан'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>
                  <FaMapMarkerAlt />
                  Город:
                </InfoLabel>
                <InfoValue>{user?.city || 'Не указан'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>
                  <FaEnvelope />
                  Email:
                </InfoLabel>
                <InfoValue>{user?.email}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>
                  <FaCalendarAlt />
                  Дата регистрации:
                </InfoLabel>
                <InfoValue>
                  {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('ru-RU') : 'Не указана'}
                </InfoValue>
              </InfoItem>
              
              <ButtonGroup>
                <Button onClick={() => setIsEditing(true)}>
                  <FaEdit />
                  Редактировать профиль
                </Button>
                <Button 
                  type="button" 
                  className="secondary" 
                  onClick={() => setIsChangingPassword(true)}
                  disabled={isLoading}
                >
                  <FaLock />
                  Поменять пароль
                </Button>
              </ButtonGroup>

              {/* Блок смены пароля (виден в режиме просмотра при нажатии «Поменять пароль») */}
              {isChangingPassword && (
                <div style={{ marginTop: '30px', paddingTop: '30px', borderTop: '1px solid #eee' }}>
                  <SectionTitle style={{ marginBottom: '20px', fontSize: '18px' }}>
                    <FaLock />
                    Смена пароля
                  </SectionTitle>
                  {passwordMessage.text && (
                    <Message className={passwordMessage.type} style={{ marginBottom: '20px' }}>
                      {passwordMessage.type === 'success' ? '✅' : '❌'} {passwordMessage.text}
                    </Message>
                  )}
                  <InputGroup>
                    <Label><FaLock /> Текущий пароль</Label>
                    <Input
                      type="password"
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      placeholder="Введите текущий пароль"
                      disabled={passwordLoading}
                    />
                  </InputGroup>
                  <InputGroup>
                    <Label><FaLock /> Новый пароль</Label>
                    <Input
                      type="password"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      placeholder="Введите новый пароль"
                      disabled={passwordLoading}
                    />
                    <ValidationHint>
                      <ValidationRule valid={validatePassword().minLength}>
                        {validatePassword().minLength ? <FaCheck /> : <FaTimesCircle />}
                        Минимум 6 символов
                      </ValidationRule>
                      <ValidationRule valid={validatePassword().hasLowerCase}>
                        {validatePassword().hasLowerCase ? <FaCheck /> : <FaTimesCircle />}
                        Строчные буквы (a-z)
                      </ValidationRule>
                      <ValidationRule valid={validatePassword().hasUpperCase}>
                        {validatePassword().hasUpperCase ? <FaCheck /> : <FaTimesCircle />}
                        Заглавные буквы (A-Z)
                      </ValidationRule>
                      <ValidationRule valid={validatePassword().hasDigit}>
                        {validatePassword().hasDigit ? <FaCheck /> : <FaTimesCircle />}
                        Цифры (0-9)
                      </ValidationRule>
                    </ValidationHint>
                  </InputGroup>
                  <InputGroup>
                    <Label><FaLock /> Подтвердите пароль</Label>
                    <Input
                      type="password"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      placeholder="Повторите новый пароль"
                      disabled={passwordLoading}
                    />
                    {passwordData.confirmPassword.length > 0 && (
                      <ValidationHint>
                        <ValidationRule valid={validatePassword().passwordsMatch}>
                          {validatePassword().passwordsMatch ? <FaCheck /> : <FaTimesCircle />}
                          {validatePassword().passwordsMatch ? 'Пароли совпадают' : 'Пароли не совпадают'}
                        </ValidationRule>
                      </ValidationHint>
                    )}
                  </InputGroup>
                  <ButtonGroup style={{ marginTop: '10px' }}>
                    <Button
                      type="button"
                      onClick={handlePasswordSubmit}
                      disabled={passwordLoading}
                    >
                      <FaSave />
                      {passwordLoading ? 'Сохранение...' : 'Изменить пароль'}
                    </Button>
                    <Button
                      type="button"
                      className="secondary"
                      onClick={handleCancelPasswordChange}
                      disabled={passwordLoading}
                    >
                      <FaTimes />
                      Отмена
                    </Button>
                  </ButtonGroup>
                </div>
              )}
            </>
          )}
          </ProfileSection>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
          <ProfileSection>
            <SectionTitle>
              <FaChartLine />
              Статистика активности
            </SectionTitle>
            
            <StatsGrid>
              <StatCard>
                <StatIcon>
                  <FaClipboardList />
                </StatIcon>
                <StatNumber>{statsLoading ? '...' : stats.totalOrders}</StatNumber>
                <StatLabel>Всего заказов</StatLabel>
              </StatCard>
              <StatCard>
                <StatIcon>
                  <FaShoppingCart />
                </StatIcon>
                <StatNumber>{statsLoading ? '...' : stats.activeOrders}</StatNumber>
                <StatLabel>Активных заказов</StatLabel>
              </StatCard>
              <StatCard>
                <StatIcon>
                  <FaHeart />
                </StatIcon>
                <StatNumber>{statsLoading ? '...' : stats.favoritesCount}</StatNumber>
                <StatLabel>В избранном</StatLabel>
              </StatCard>
              <StatCard>
                <StatIcon>
                  <FaStar />
                </StatIcon>
                <StatNumber>{statsLoading ? '...' : stats.reviewsCount}</StatNumber>
                <StatLabel>Отзывов оставлено</StatLabel>
              </StatCard>
            </StatsGrid>
            
            <ButtonGroup>
              <Button as={Link} to="/dashboard">
                <FaCog />
                Личный кабинет
              </Button>
            </ButtonGroup>
          </ProfileSection>

          <ProfileSection>
            <SectionTitle>
              <FaGift />
              Быстрые действия
            </SectionTitle>
            
            <QuickActions>
              <ActionCard to="/orders">
                <ActionIcon>
                  <FaClipboardList />
                </ActionIcon>
                <ActionTitle>История заказов</ActionTitle>
                <ActionDesc>Просмотр всех заказов</ActionDesc>
              </ActionCard>
              
              <ActionCard to="/cart">
                <ActionIcon>
                  <FaShoppingCart />
                </ActionIcon>
                <ActionTitle>Корзина</ActionTitle>
                <ActionDesc>Управление покупками</ActionDesc>
              </ActionCard>
              
              <ActionCard to="/favorites">
                <ActionIcon>
                  <FaHeart />
                </ActionIcon>
                <ActionTitle>Избранное</ActionTitle>
                <ActionDesc>Мои избранные товары</ActionDesc>
              </ActionCard>
              
              
              <ActionCard to="/dashboard/team">
                <ActionIcon>
                  <FaShieldAlt />
                </ActionIcon>
                <ActionTitle>Моя команда</ActionTitle>
                <ActionDesc>Реферальная программа</ActionDesc>
              </ActionCard>
            </QuickActions>
          </ProfileSection>
        </div>
      </ProfileContent>
    </ProfileContainer>
  );
};

export default Profile; 